import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { localize, useOutsideClickEffect } from '@saviynt/common';
import {
  Box,
  Button,
  ButtonCore,
  ButtonIcon,
  Icon,
  Link,
  Typography,
} from '@saviynt/design-system';
import classnames from 'classnames';
import moment from 'moment';

import { TIMEOUT_CANCELLATION } from '../../http-service/httpService';
import {
  bulkActionConsultApi,
  bulkActionReassignApi,
  bulkActionUpdateApi,
  getCertDetailsData,
  getProgressBreakdownDetails,
  getTabCounts,
  selectAllBulkAction,
  setAlteastOneIsSelected,
  setBannerForConsultAndReassign,
  setBulkActionAlert,
  setBulkActionBySelectAll,
  setBulkActionFooterData,
  setBulkFooterShow,
  setCertTriggerFromSearchOrFilter,
  setCheckForBulkAction,
  setConsultActionAssigneeLoading,
  setIndeterminateForBulkAction,
  setIsBulkActionsCompleted,
} from '../../redux/certdetails/action';
import {
  CAMPAIGN_TYPES,
  CAMPAIGNTYPE_ENTITLEMENTOWNER,
  CAMPAIGNTYPE_SERVICEACCOUNT,
  CAMPAIGNTYPE_USERMANAGER,
  ITEMS_API_OFFSET,
  SCREEN_SIZE_MOBILE_XLARGE,
  SCREEN_WIDTH_QUERIES,
  SYSTEM_DATA_THEMES,
} from '../../utils/constant';
import { useInvertedLocalize } from '../../utils/localization';
import { certsFormattedMessage, getMessageKey } from '../../utils/utils';
import {
  EO_CERTIFIED,
  PREDEFINED_COMMENTS_FOR_ACTION,
} from '../CertsActionButtons';
import { BY_DECISION } from '../CertsDetails/PopoverProgressBreakdown';

import BulkActionCommon from './BulkActionCommon';
import BulkActionConsult from './BulkActionConsult';
import BulkActionEndDate from './BulkActionEndDate';
import BulkActionReassign from './BulkActionReassign';
import {
  BULK_ACTION_BUTTON_COUNT_PER_ROW,
  BULK_ACTION_FOOTER_BTNS_REVERSE,
  BULK_ACTION_FOOTER_OBJECTS,
  BULK_ACTION_FOOTER_OBJECTS_MESSGAE_KEYS,
  COMMENT_MINIMUM_LENGTH,
  DEFAULT_OBJECT_FOR_COMMON_ACTION,
  DEFAULT_OBJECT_FOR_CONSULT,
  DEFAULT_OBJECT_FOR_END_DATE,
  DEFAULT_OBJECT_FOR_REASSIGN,
  getBulkActionFooterBtns,
  MAX_BTN_TO_SHOW_FOR_BULK_ACTION_FOOTER,
  MIN_BTN_TO_SHOW_FOR_BULK_ACTION_FOOTER,
  SCREEN_WIDTH_FOR_BUTTONS_TO_SHOW_1,
  SCREEN_WIDTH_FOR_BUTTONS_TO_SHOW_2,
} from './constant';
import invertedMessages from './messages';
import { bulkFooterSortedBtns } from './utils';

import './BulkActionFooter.css';

function BulkActionFooter() {
  // Modal Common Actions => Approve, Confirm Manager, Reject, Terminate
  const [showKebabActionBtns, setShowKebabActionBtns] = useState(false);
  const [bulkActionBtnsMap, setBulkActionBtnsMap] = useState([]);
  const [bulkFooterStats, setBulkFooterStats] = useState({});
  const [isMobileMoreChecked, setIsMobileMoreChecked] = useState(false);
  const [
    countForActionButtonsAsPerScreen,
    setCountForActionButtonsAsPerScreen,
  ] = useState(MIN_BTN_TO_SHOW_FOR_BULK_ACTION_FOOTER);
  const [bulkActionCommonAction, setBulkActionCommonAction] = useState(
    DEFAULT_OBJECT_FOR_COMMON_ACTION
  );
  const [bulkActionEndDate, setBulkActionEndDate] = useState(
    DEFAULT_OBJECT_FOR_END_DATE
  );
  const [bulkActionConsult, setBulkActionConsult] = useState(
    DEFAULT_OBJECT_FOR_CONSULT
  );
  const [bulkActionReassign, setBulkActionReassign] = useState(
    DEFAULT_OBJECT_FOR_REASSIGN
  );
  const intl = useIntl();
  const dispatch = useDispatch();

  const { messages } = useSelector((state) => state?.user);

  const l = useInvertedLocalize(invertedMessages);
  const {
    bulkAction: {
      bulkFooterInfo,
      isBulkFooterVisible,
      isDisabled,
      isSubmittingAction,
    },
    core: {
      columnNames,
      isLoading,
      predefinedComments,
      searchText,
      campaignType,
    },
    global: { certKey, mediaQueryValue, backNavigation },
    lineActions: { reassignConfigMap },
    reviewAndSubmit: { primaryCertifier: isPrimaryCertifier },
    sidePanel: { isSidePanelVisible },
  } = useSelector((state) => state?.certifications?.details);

  const [bulkPredefinedComments, setBulkPredefinedComments] = useState([]);

  const mobileLargeQuery = SCREEN_WIDTH_QUERIES.find(
    (item) => item.label === SCREEN_SIZE_MOBILE_XLARGE
  );
  const isMobileXlargeWidth = mobileLargeQuery.value <= mediaQueryValue;

  const isEOCampaign =
    CAMPAIGN_TYPES[campaignType] === CAMPAIGNTYPE_ENTITLEMENTOWNER;
  // Get the appropriate buttons based on campaign type
  const currentBulkActionFooterBtns = getBulkActionFooterBtns(
    campaignType,
    reassignConfigMap
  );

  const wrapperClass = classnames(
    'BulkActionFooter-wrapper',
    isMobileXlargeWidth &&
      !isMobileMoreChecked &&
      isBulkFooterVisible &&
      bulkActionBtnsMap?.length > 0 &&
      'BulkActionFooter-wrapper--mobileMode1',
    isMobileXlargeWidth &&
      isMobileMoreChecked &&
      isBulkFooterVisible &&
      bulkActionBtnsMap?.length < 8 && // We can keep this without constant here as we will not have count of actions more than 8
      'BulkActionFooter-wrapper--mobileMode2',
    isMobileXlargeWidth &&
      isMobileMoreChecked &&
      isBulkFooterVisible &&
      bulkActionBtnsMap?.length >= 8 && // We can keep this without constant here as we will not have count of actions more than 8
      'BulkActionFooter-wrapper--mobileMode3'
  );

  useEffect(() => {
    const validBulkActions = new Set([]);

    if (isEOCampaign) {
      ['enableAccountActionList', 'enableChildActionList'].forEach((list) => {
        bulkFooterInfo?.[list]?.forEach((action) =>
          validBulkActions.add(action)
        );
      });

      bulkFooterInfo?.enableParentEntitlementActionList?.forEach((action) => {
        if (action === 'Belongs to Me') {
          validBulkActions.add('approve');
        } else if (action === 'Decommission') {
          validBulkActions.add('decommission');
        } else {
          validBulkActions.add(action);
        }
      });
    } else {
      ['enableAccountEntRoleActionList', 'enableUserActionList'].forEach(
        (list) => {
          bulkFooterInfo?.[list]?.forEach((action) =>
            validBulkActions.add(action)
          );
        }
      );
    }

    setBulkActionBtnsMap(Array.from(validBulkActions));

    if (bulkFooterInfo) {
      const tempBulkFooterStats = {};

      // eslint-disable-next-line no-restricted-syntax
      for (const key in BULK_ACTION_FOOTER_OBJECTS) {
        if (bulkFooterInfo[key]) {
          tempBulkFooterStats[key] = bulkFooterInfo[key];
        }
      }

      setBulkFooterStats(tempBulkFooterStats);

      return;
    }

    setCountForActionButtonsAsPerScreen(1);
  }, [bulkFooterInfo]);

  useEffect(() => {
    setBulkActionCommonAction((prev) => ({
      ...prev,
      isSubmitting: isSubmittingAction,
    }));
    setBulkActionEndDate((prev) => ({
      ...prev,
      isSubmitting: isSubmittingAction,
    }));
    setBulkActionConsult((prev) => ({
      ...prev,
      isSubmitting: isSubmittingAction,
    }));
    setBulkActionReassign((prev) => ({
      ...prev,
      isSubmitting: isSubmittingAction,
    }));
  }, [isSubmittingAction]);

  useEffect(() => {
    setBulkPredefinedComments(predefinedComments || []);
  }, [predefinedComments]);

  const dropUpWrapperForBulkActionKebab = useRef(
    'dropUpWrapperForBulkActionKebab'
  );
  const bulkActionBtnBoxRef = useRef('bulkActionBtnBoxRef');

  useOutsideClickEffect(dropUpWrapperForBulkActionKebab, () => {
    setShowKebabActionBtns(false);
  });

  useEffect(() => {
    const handleResize = () => {
      const screenWidth = bulkActionBtnBoxRef?.current?.offsetWidth;

      // TO DO @vishnu remove this code after implementing mobile responsiveness
      if (screenWidth < SCREEN_WIDTH_FOR_BUTTONS_TO_SHOW_1) {
        setCountForActionButtonsAsPerScreen(
          MAX_BTN_TO_SHOW_FOR_BULK_ACTION_FOOTER
        );
      } else if (screenWidth > SCREEN_WIDTH_FOR_BUTTONS_TO_SHOW_2) {
        setCountForActionButtonsAsPerScreen(
          MAX_BTN_TO_SHOW_FOR_BULK_ACTION_FOOTER
        );
      } else {
        setCountForActionButtonsAsPerScreen(
          MAX_BTN_TO_SHOW_FOR_BULK_ACTION_FOOTER
        );
      }
    };

    if (!isBulkFooterVisible) {
      setIsMobileMoreChecked(false);
    }

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [isBulkFooterVisible, isSidePanelVisible]);

  const getAlertMessage = (footerStats, id) => {
    if (
      typeof footerStats === 'object' ||
      Array.isArray(footerStats) ||
      footerStats === null ||
      footerStats === undefined
    )
      return (
        messages['BULKACTION.action.failed.ATTESTATIONS'] ||
        localize(intl, getMessageKey('BULKACTION.action.failed'))
      );

    const alertTextArray = [];
    const buttonKey = BULK_ACTION_FOOTER_BTNS_REVERSE?.[id];
    const buttonMap = currentBulkActionFooterBtns?.[buttonKey];

    buttonMap?.objectsToTotal.forEach((item) => {
      if (footerStats[item]) {
        const messageKey =
          BULK_ACTION_FOOTER_OBJECTS_MESSGAE_KEYS?.[
            BULK_ACTION_FOOTER_OBJECTS?.[item]
          ];

        alertTextArray.push(
          `${footerStats[item]} ${
            messages.messageKey || localize(intl, getMessageKey(messageKey))
          }(s)`
        );
      }
    });

    let midText = '';

    if (alertTextArray.length === 1) {
      // eslint-disable-next-line prefer-destructuring
      midText = alertTextArray[0];
    }

    if (alertTextArray.length > 1) {
      midText = alertTextArray.slice(0, alertTextArray.length - 1).join(', ');
      midText = `${midText} ${
        messages.CERTS_BULK_AND ||
        localize(intl, getMessageKey('CERTS_BULK_AND'))
      } ${alertTextArray[alertTextArray.length - 1]}`;
    }

    return certsFormattedMessage(
      messages.CERTS_BULK_ACTION_FAILED ||
        localize(intl, getMessageKey('CERTS_BULK_ACTION_FAILED')),
      {
        action:
          messages?.[buttonMap.text] ||
          localize(intl, getMessageKey(buttonMap.text)),
        lineitem: midText,
      }
    );
  };

  const resetBulkCheckBoxes = () => {
    dispatch(setBulkActionBySelectAll(true));
    dispatch(setIndeterminateForBulkAction(false));
    dispatch(setCheckForBulkAction(false));
    dispatch(setAlteastOneIsSelected(false));
    dispatch(setBulkFooterShow(false));
    dispatch(setBulkActionFooterData(null));
    dispatch(setIsBulkActionsCompleted(true));
  };

  const resetTabAndItems = () => {
    dispatch(setCertTriggerFromSearchOrFilter(true));
    dispatch(getTabCounts(certKey, true));
    dispatch(
      getCertDetailsData(certKey, columnNames, ITEMS_API_OFFSET, 0, searchText)
    );
    dispatch(getProgressBreakdownDetails(certKey, BY_DECISION, true));
    dispatch(setConsultActionAssigneeLoading(true)); // For Consult and Reassign loading icon on load
  };

  const handleClear = () => {
    dispatch(setBulkActionBySelectAll(true));

    dispatch(selectAllBulkAction(certKey, true, isEOCampaign))
      .then(() => {
        dispatch(setCheckForBulkAction(false));
        dispatch(setIndeterminateForBulkAction(false));
        dispatch(setAlteastOneIsSelected(false));
        dispatch(setBulkFooterShow(false));
      })
      .catch((error) => {
        dispatch(setBulkActionAlert(true, error.message));
      });
  };

  // handleCommonAction for below actions
  // const handleApprove = () => {}
  // const handleWorksForMe = () => {};
  // const handleReject = () => {};
  // const handleTerminate = () => {};

  const handleCommonAction = (id, status, comment) => {
    if (
      bulkActionCommonAction?.comment?.replace(/^\s+|\s+$/gm, '')?.length <=
        COMMENT_MINIMUM_LENGTH ||
      !bulkActionCommonAction?.hasAgreed
    ) {
      setBulkActionCommonAction((prev) => ({
        ...prev,
        showAlert: true,
      }));

      return;
    }

    setBulkActionCommonAction((prev) => ({
      ...prev,
      showAlert: false,
    }));

    const payload = { userAction: '1' };

    dispatch(
      bulkActionUpdateApi(
        certKey,
        isEOCampaign ? String(status) : status,
        comment,
        id === currentBulkActionFooterBtns.TERMINATE.id ? payload : {},
        id === currentBulkActionFooterBtns.DECOMMISSION.id && isEOCampaign
      )
    )
      .then(() => {
        resetBulkCheckBoxes();
        resetTabAndItems();
      })
      .catch((error) => {
        // Time out error handler for Bulk Actions
        if (error === TIMEOUT_CANCELLATION) {
          dispatch(
            setBannerForConsultAndReassign({
              title: l('Success'),
              description: l(
                'The operation is taking longer than usual. Processing your request in the background.'
              ),
            })
          );
          resetBulkCheckBoxes();
          resetTabAndItems();

          return;
        }

        if (error.message && typeof error.message === 'string') {
          dispatch(setBulkActionAlert(true, error.message));
        } else {
          dispatch(setBulkActionAlert(true, getAlertMessage(error, id)));
        }
      })
      .finally(() => {
        setBulkActionCommonAction(DEFAULT_OBJECT_FOR_COMMON_ACTION);
      });
  };

  // handleEndDate for below actions
  // const handleExtendEndDate = () => {};
  // const handleConditionalyCertify = () => {};
  const handleEndDate = (id, status, date, comment) => {
    if (
      bulkActionEndDate?.comment?.replace(/^\s+|\s+$/gm, '')?.length <=
        COMMENT_MINIMUM_LENGTH ||
      !bulkActionEndDate?.endDate ||
      !bulkActionEndDate?.hasAgreed
    ) {
      setBulkActionEndDate((prev) => ({
        ...prev,
        showAlert: true,
      }));

      return;
    }

    setBulkActionEndDate((prev) => ({
      ...prev,
      showAlert: false,
    }));

    const conditionallyCertifyEndDate = moment(date).format('MM-DD-YYYY');
    const extendEndDate = moment(date).format('MM-DD-YYYY');
    // moment(date).format(globalDateFormat.toUpperCase()); // as part ofCR-19433

    dispatch(
      bulkActionUpdateApi(
        certKey,
        status,
        comment,
        id === currentBulkActionFooterBtns.CONDITIONAL_CERTIFIY.id
          ? { enddate: conditionallyCertifyEndDate }
          : { extendedDate: extendEndDate }
      )
    )
      .then(() => {
        resetBulkCheckBoxes();
        resetTabAndItems();
      })
      .catch((error) => {
        // Time out error handler for Bulk Actions
        if (error === TIMEOUT_CANCELLATION) {
          dispatch(
            setBannerForConsultAndReassign({
              title: l('Success'),
              description: l(
                'The operation is taking longer than usual. Processing your request in the background.'
              ),
            })
          );
          resetBulkCheckBoxes();
          resetTabAndItems();

          return;
        }

        if (error.message && typeof error.message === 'string') {
          dispatch(setBulkActionAlert(true, error.message));
        } else {
          dispatch(setBulkActionAlert(true, getAlertMessage(error, id)));
        }
      })
      .finally(() => {
        setBulkActionEndDate(DEFAULT_OBJECT_FOR_END_DATE);
      });
  };

  // handle reassign action
  // const handleDoesNotWorkForMe = () => {};
  const handleReassign = (
    id,
    reassignTo,
    reassignAssociated,
    comment,
    reassignToName = ''
    // isUpdateOwner = false
  ) => {
    const isSACampaign =
      CAMPAIGN_TYPES[campaignType] === CAMPAIGNTYPE_SERVICEACCOUNT;

    const shouldConvertToReassign = Boolean(
      isPrimaryCertifier &&
        CAMPAIGN_TYPES[campaignType] === CAMPAIGNTYPE_USERMANAGER &&
        (reassignConfigMap?.enableReassignUser === '1' ||
          reassignConfigMap?.showReassignPopup === '1')
    );

    // update Account owner
    // const isShowUpdateAccountOwnerEnabled =
    //   !backNavigation.consulteeLoggedIn &&
    //   !reassignConfigMap.enableAccessVerificationOnly &&
    //   reassignConfigMap.enableReassignUser.includes('1') &&
    //   reassignConfigMap.showUpdateAccountOwner === '1' &&
    //   reassignConfigMap.showReassignPopup === '1' &&
    //   CAMPAIGN_TYPES[campaignType] === CAMPAIGNTYPE_SERVICEACCOUNT;

    // // SA certifier required
    const isCertifierNeededForSACampaign =
      CAMPAIGN_TYPES[campaignType] === CAMPAIGNTYPE_SERVICEACCOUNT &&
      reassignConfigMap.showReassignPopup === '1' &&
      ((reassignConfigMap.enableEmploymentVerificationOnly &&
        reassignConfigMap.enableReassignUser?.includes('1')) ||
        reassignConfigMap.enableReassignUser?.includes('1') ||
        reassignConfigMap.enableReassignStep2?.includes('1')) &&
      (reassignConfigMap?.isSecondaryCertifierLoggedIn ||
        !backNavigation.consulteeLoggedIn);

    if (
      id === currentBulkActionFooterBtns.REASSIGN.id &&
      (bulkActionReassign?.comment?.replace(/^\s+|\s+$/gm, '')?.length <=
        COMMENT_MINIMUM_LENGTH ||
        bulkActionReassign?.reassignTo?.length === 0 ||
        !bulkActionReassign?.hasAgreed)
    ) {
      setBulkActionReassign((prev) => ({
        ...prev,
        showAlert: true,
      }));

      return;
    }

    if (
      id === currentBulkActionFooterBtns.DOES_NOT_WORK_FOR_ME.id &&
      shouldConvertToReassign &&
      (bulkActionReassign?.comment?.replace(/^\s+|\s+$/gm, '')?.length === 0 ||
        !bulkActionReassign?.hasAgreed)
    ) {
      setBulkActionReassign((prev) => ({
        ...prev,
        showAlert: true,
      }));

      return;
    }

    if (
      id === currentBulkActionFooterBtns.DOES_NOT_WORK_FOR_ME.id &&
      shouldConvertToReassign &&
      (bulkActionReassign?.comment?.replace(/^\s+|\s+$/gm, '')?.length === 0 ||
        bulkActionReassign?.reassignTo?.length === 0 ||
        !bulkActionReassign?.hasAgreed)
    ) {
      setBulkActionReassign((prev) => ({
        ...prev,
        showAlert: true,
      }));

      return;
    }

    if (
      id === currentBulkActionFooterBtns.DOES_NOT_WORK_FOR_ME.id &&
      !shouldConvertToReassign &&
      (bulkActionReassign?.comment?.replace(/^\s+|\s+$/gm, '')?.length === 0 ||
        !bulkActionReassign?.hasAgreed)
    ) {
      setBulkActionReassign((prev) => ({
        ...prev,
        showAlert: true,
      }));

      return;
    }

    // SA campaign Reassign -> Step 1 + Step 2
    if (
      id === currentBulkActionFooterBtns.DOES_NOT_BELONG_TO_ME.id &&
      isCertifierNeededForSACampaign &&
      (bulkActionReassign?.comment?.replace(/^\s+|\s+$/gm, '')?.length === 0 ||
        !bulkActionReassign?.hasAgreed)
    ) {
      setBulkActionReassign((prev) => ({
        ...prev,
        showAlert: true,
      }));

      return;
    }

    if (
      id === currentBulkActionFooterBtns.DOES_NOT_BELONG_TO_ME.id &&
      isCertifierNeededForSACampaign &&
      (bulkActionReassign?.comment?.replace(/^\s+|\s+$/gm, '')?.length === 0 ||
        bulkActionReassign?.reassignTo?.length === 0 ||
        !bulkActionReassign?.hasAgreed)
    ) {
      setBulkActionReassign((prev) => ({
        ...prev,
        showAlert: true,
      }));

      return;
    }

    if (
      id === currentBulkActionFooterBtns.DOES_NOT_BELONG_TO_ME.id &&
      !isCertifierNeededForSACampaign &&
      (bulkActionReassign?.comment?.replace(/^\s+|\s+$/gm, '')?.length === 0 ||
        !bulkActionReassign?.hasAgreed)
    ) {
      setBulkActionReassign((prev) => ({
        ...prev,
        showAlert: true,
      }));

      return;
    }

    // // SA only when update account owner shown
    // if (
    //   id === currentBulkActionFooterBtns.DOES_NOT_BELONG_TO_ME.id &&
    //   isShowUpdateAccountOwnerEnabled &&
    //   !isUpdateOwner
    // ) {
    //   setBulkActionReassign((prev) => ({
    //     ...prev,
    //     showAlert: true,
    //   }));

    //   return;
    // }

    setBulkActionReassign((prev) => ({
      ...prev,
      showAlert: false,
    }));

    if (
      reassignConfigMap?.enableReassignUser !== '1' &&
      reassignConfigMap?.showReassignPopup !== '1' &&
      !isSACampaign
    ) {
      dispatch(
        bulkActionUpdateApi(
          certKey,
          currentBulkActionFooterBtns.DOES_NOT_WORK_FOR_ME.status,
          comment,
          { userAction: '1' },
          isEOCampaign
        )
      )
        .then((data) => {
          resetBulkCheckBoxes();
          resetTabAndItems();

          if (reassignToName !== '') {
            dispatch(
              setBannerForConsultAndReassign({
                title: 'Success',
                description: `Item(s) reassigned to ${reassignToName}`,
              })
            );
          } else if (data?.response && typeof data?.response === 'string') {
            dispatch(
              setBannerForConsultAndReassign({
                title: 'Success',
                description: data.response,
              })
            );
          }
        })
        .catch((error) => {
          // Time out error handler for Bulk Actions
          if (error === TIMEOUT_CANCELLATION) {
            dispatch(
              setBannerForConsultAndReassign({
                title: l('Success'),
                description: l(
                  'The operation is taking longer than usual. Processing your request in the background.'
                ),
              })
            );
            resetBulkCheckBoxes();
            resetTabAndItems();

            return;
          }

          if (error.message && typeof error.message === 'string') {
            dispatch(setBulkActionAlert(true, l(error.message)));
          } else {
            dispatch(
              setBulkActionAlert(
                true,
                getAlertMessage(
                  error,
                  currentBulkActionFooterBtns.DOES_NOT_WORK_FOR_ME.id
                )
              )
            );
          }
        })
        .finally(() => {
          setBulkActionReassign(DEFAULT_OBJECT_FOR_REASSIGN);
        });

      return;
    }

    const defaultPayload = isSACampaign
      ? {
          // SA campaign
          cert_entitlement_valuekey: '0',
          accountAction: '1',
        }
      : {
          // UM campaign
          assigneetype: '1',
          cert_userkey: '0',
          cert_entitlement_valuekey: '-4',
          userAction: '1',
        };

    dispatch(
      bulkActionReassignApi(
        certKey,
        reassignTo,
        comment,
        id === currentBulkActionFooterBtns.REASSIGN.id
          ? 'bulkReassign'
          : 'reassign',
        id === currentBulkActionFooterBtns.REASSIGN.id
          ? {
              reassignAssociated: reassignAssociated ? 'true' : 'false',
              enableReassignStep2: reassignConfigMap?.enableReassignStep2,
            }
          : defaultPayload,
        isEOCampaign
      )
    )
      .then((data) => {
        resetBulkCheckBoxes();
        resetTabAndItems();

        if (reassignToName !== '') {
          dispatch(
            setBannerForConsultAndReassign({
              title: 'Success',
              description: `Item(s) reassigned to ${
                reassignToName?.length === 0
                  ? 'default certifier'
                  : reassignToName
              }`,
            })
          );
        } else if (data?.response && typeof data?.response === 'string') {
          dispatch(
            setBannerForConsultAndReassign({
              title: 'Success',
              description: data.response,
            })
          );
        }
      })
      .catch((error) => {
        // Time out error handler for Bulk Actions
        if (error === TIMEOUT_CANCELLATION) {
          dispatch(
            setBannerForConsultAndReassign({
              title: l('Success'),
              description: l(
                'The operation is taking longer than usual. Processing your request in the background.'
              ),
            })
          );
          resetBulkCheckBoxes();
          resetTabAndItems();

          return;
        }

        if (error.message && typeof error.message === 'string') {
          dispatch(setBulkActionAlert(true, error.message));
        } else {
          dispatch(
            setBulkActionAlert(
              true,
              getAlertMessage(
                error,
                currentBulkActionFooterBtns.DOES_NOT_WORK_FOR_ME.id
              )
            )
          );
        }
      })
      .finally(() => {
        setBulkActionReassign(DEFAULT_OBJECT_FOR_REASSIGN);
      });
  };

  // handle consult action
  const handleConsult = (
    reassignAssociated,
    consultWith,
    comment,
    consultWithName = ''
  ) => {
    if (
      bulkActionConsult?.comment?.replace(/^\s+|\s+$/gm, '')?.length <=
        COMMENT_MINIMUM_LENGTH ||
      bulkActionConsult?.consultWith?.length === 0 ||
      !bulkActionConsult?.hasAgreed
    ) {
      setBulkActionConsult((prev) => ({
        ...prev,
        showAlert: true,
      }));

      return;
    }

    setBulkActionConsult((prev) => ({
      ...prev,
      showAlert: false,
    }));

    dispatch(
      bulkActionConsultApi(
        certKey,
        consultWith,
        comment,
        reassignAssociated ? 'true' : 'false'
      )
    )
      .then(() => {
        resetBulkCheckBoxes();
        resetTabAndItems();
        dispatch(
          setBannerForConsultAndReassign({
            title: 'Success',
            description: `Item(s) consulted to ${
              consultWithName?.length === 0
                ? 'default certifier'
                : consultWithName
            }`,
          })
        );
      })
      .catch((error) => {
        // Time out error handler for Bulk Actions
        if (error === TIMEOUT_CANCELLATION) {
          dispatch(
            setBannerForConsultAndReassign({
              title: l('Success'),
              description: l(
                'The operation is taking longer than usual. Processing your request in the background.'
              ),
            })
          );

          resetBulkCheckBoxes();
          resetTabAndItems();

          return;
        }

        if (error.message && typeof error.message === 'string') {
          dispatch(setBulkActionAlert(true, error.message));
        } else {
          dispatch(
            setBulkActionAlert(
              true,
              getAlertMessage(error, currentBulkActionFooterBtns.CONSULT.id)
            )
          );
        }
      })
      .finally(() => {
        setBulkActionConsult(DEFAULT_OBJECT_FOR_CONSULT);
      });
  };

  // Modals open
  const handleCommonModalOpen = (id, status, commentKey) => {
    setBulkActionCommonAction((prev) => ({
      ...prev,
      isOpen: !prev.isOpen,
      showAlert: false,
      id,
      status,
      commentKey: commentKey || '',
    }));
    setShowKebabActionBtns(false);
  };

  const handleEndDateModalOpen = (id, status, commentKey) => {
    setBulkActionEndDate((prev) => ({
      ...prev,
      isOpen: !prev.isOpen,
      showAlert: false,
      id,
      status,
      commentKey: commentKey || '',
    }));
    setShowKebabActionBtns(false);
  };

  const handleConsultModalOpen = (id, status, commentKey) => {
    setBulkActionConsult((prev) => ({
      ...prev,
      isOpen: !prev.isOpen,
      showAlert: false,
      id,
      status,
      commentKey: commentKey || '',
    }));
    setShowKebabActionBtns(false);
  };

  const handleReassignModalOpen = (id, status, commentKey) => {
    setBulkActionReassign((prev) => ({
      ...prev,
      isOpen: !prev.isOpen,
      showAlert: false,
      // isUpdateOwner: false,
      id,
      status,
      commentKey: commentKey || '',
    }));
    setShowKebabActionBtns(false);
  };

  const handleDecommissionModalOpen = (id, status, commentKey) => {
    setBulkActionCommonAction((prev) => ({
      ...prev,
      isOpen: !prev.isOpen,
      showAlert: false,
      id,
      status,
      commentKey: commentKey || '',
    }));
    setShowKebabActionBtns(false);
  };

  const handleBelongsToMeModalOpen = (id, status, commentKey) => {
    setBulkActionCommonAction((prev) => ({
      ...prev,
      isOpen: !prev.isOpen,
      showAlert: false,
      id,
      status,
      commentKey: commentKey || '',
    }));
    setShowKebabActionBtns(false);
  };

  const bulkActionClearBtn = {
    id: currentBulkActionFooterBtns.CLEAR.id,
    icon: currentBulkActionFooterBtns.CLEAR.icon,
    status: currentBulkActionFooterBtns.CLEAR.status,
    text: currentBulkActionFooterBtns.CLEAR.text,
    objectsToTotal: currentBulkActionFooterBtns.CLEAR.objectsToTotal,
    kind: 'ghost',
    popOverContent: '',
    onClick: handleClear,
  };

  const bulkActionButtons = {
    worksForMe: {
      id: currentBulkActionFooterBtns.WORKS_FOR_ME.id,
      icon: currentBulkActionFooterBtns.WORKS_FOR_ME.icon,
      status: isEOCampaign
        ? EO_CERTIFIED.WORKS_FOR_ME
        : currentBulkActionFooterBtns.WORKS_FOR_ME.status,
      text: currentBulkActionFooterBtns.WORKS_FOR_ME.text,
      objectsToTotal: currentBulkActionFooterBtns.WORKS_FOR_ME.objectsToTotal,
      commentKey: currentBulkActionFooterBtns.WORKS_FOR_ME.commentKey,
      kind: 'outlined',
      popOverContent: '',
      onClick: handleCommonModalOpen,
    },
    doesNotWorksForMe: {
      id: currentBulkActionFooterBtns.DOES_NOT_WORK_FOR_ME.id,
      icon: currentBulkActionFooterBtns.DOES_NOT_WORK_FOR_ME.icon,
      status: currentBulkActionFooterBtns.DOES_NOT_WORK_FOR_ME.status,
      text: currentBulkActionFooterBtns.DOES_NOT_WORK_FOR_ME.text,
      objectsToTotal:
        currentBulkActionFooterBtns.DOES_NOT_WORK_FOR_ME.objectsToTotal,
      commentKey: (() => {
        const isSACampaign =
          CAMPAIGN_TYPES[campaignType] === CAMPAIGNTYPE_SERVICEACCOUNT;

        if (isEOCampaign)
          return PREDEFINED_COMMENTS_FOR_ACTION.BULK_DOES_NOT_BELONG_TO_ME;
        if (isSACampaign)
          return PREDEFINED_COMMENTS_FOR_ACTION.BULK_DOES_NOT_BELONG_TO_ME;
        if (reassignConfigMap?.enableReassignUser === '1')
          return PREDEFINED_COMMENTS_FOR_ACTION.BULK_DOES_NOT_WORK_FOR_ME;

        return PREDEFINED_COMMENTS_FOR_ACTION.BULK_DOES_NOT_WORK_FORME_WITHOUT_REASSIGNMENT;
      })(),
      kind: 'outlined',
      popOverContent: '',
      onClick: handleReassignModalOpen,
    },
    // SA campaign Reassign
    doesNotBelongToMe: {
      id: currentBulkActionFooterBtns.DOES_NOT_BELONG_TO_ME.id,
      icon: currentBulkActionFooterBtns.DOES_NOT_BELONG_TO_ME.icon,
      status: currentBulkActionFooterBtns.DOES_NOT_BELONG_TO_ME.status,
      text: currentBulkActionFooterBtns.DOES_NOT_BELONG_TO_ME.text,
      objectsToTotal:
        currentBulkActionFooterBtns.DOES_NOT_BELONG_TO_ME.objectsToTotal,
      commentKey: currentBulkActionFooterBtns.DOES_NOT_BELONG_TO_ME.commentKey,
      kind: 'outlined',
      popOverContent: '',
      onClick: handleReassignModalOpen,
    },
    terminated: {
      id: currentBulkActionFooterBtns.TERMINATE.id,
      icon: currentBulkActionFooterBtns.TERMINATE.icon,
      status: currentBulkActionFooterBtns.TERMINATE.status,
      text: currentBulkActionFooterBtns.TERMINATE.text,
      objectsToTotal: currentBulkActionFooterBtns.TERMINATE.objectsToTotal,
      commentKey: currentBulkActionFooterBtns.TERMINATE.commentKey,
      kind: 'outlined',
      popOverContent: '',
      onClick: handleCommonModalOpen,
    },
    extendEndDate: {
      id: currentBulkActionFooterBtns.EXTEND_END_DATE.id,
      icon: currentBulkActionFooterBtns.EXTEND_END_DATE.icon,
      status: currentBulkActionFooterBtns.EXTEND_END_DATE.status,
      text: currentBulkActionFooterBtns.EXTEND_END_DATE.text,
      objectsToTotal:
        currentBulkActionFooterBtns.EXTEND_END_DATE.objectsToTotal,
      commentKey: currentBulkActionFooterBtns.EXTEND_END_DATE.commentKey,
      kind: 'outlined',
      popOverContent: '',
      onClick: handleEndDateModalOpen,
    },
    approve: {
      id: currentBulkActionFooterBtns.APPROVE.id,
      icon: currentBulkActionFooterBtns.APPROVE.icon,
      // seperating EO campaign status payload
      status: isEOCampaign
        ? EO_CERTIFIED.APPROVE
        : currentBulkActionFooterBtns.APPROVE.status,
      text: currentBulkActionFooterBtns.APPROVE.text,
      objectsToTotal: currentBulkActionFooterBtns.APPROVE.objectsToTotal,
      commentKey: currentBulkActionFooterBtns.APPROVE.commentKey,
      kind: 'outlined',
      popOverContent: '',
      onClick: handleCommonModalOpen,
    },
    reject: {
      id: currentBulkActionFooterBtns.REJECT.id,
      icon: currentBulkActionFooterBtns.REJECT.icon,
      // seperating EO campaign status payload
      status: isEOCampaign
        ? EO_CERTIFIED.REVOKED
        : currentBulkActionFooterBtns.REJECT.status,
      text: currentBulkActionFooterBtns.REJECT.text,
      objectsToTotal: currentBulkActionFooterBtns.REJECT.objectsToTotal,
      commentKey: currentBulkActionFooterBtns.REJECT.commentKey,
      kind: 'outlined',
      popOverContent: '',
      onClick: handleCommonModalOpen,
    },
    conditionalCertifiy: {
      id: currentBulkActionFooterBtns.CONDITIONAL_CERTIFIY.id,
      icon: currentBulkActionFooterBtns.CONDITIONAL_CERTIFIY.icon,
      status: currentBulkActionFooterBtns.CONDITIONAL_CERTIFIY.status,
      text: currentBulkActionFooterBtns.CONDITIONAL_CERTIFIY.text,
      objectsToTotal:
        currentBulkActionFooterBtns.CONDITIONAL_CERTIFIY.objectsToTotal,
      commentKey: currentBulkActionFooterBtns.CONDITIONAL_CERTIFIY.commentKey,
      kind: 'outlined',
      popOverContent: '',
      onClick: handleEndDateModalOpen,
    },
    reassign: {
      id: currentBulkActionFooterBtns.REASSIGN.id,
      icon: currentBulkActionFooterBtns.REASSIGN.icon,
      status: currentBulkActionFooterBtns.REASSIGN.status,
      text: currentBulkActionFooterBtns.REASSIGN.text,
      objectsToTotal: currentBulkActionFooterBtns.REASSIGN.objectsToTotal,
      commentKey: currentBulkActionFooterBtns.REASSIGN.commentKey,
      kind: 'outlined',
      popOverContent: '',
      onClick: handleReassignModalOpen,
    },
    consult: {
      id: currentBulkActionFooterBtns.CONSULT.id,
      icon: currentBulkActionFooterBtns.CONSULT.icon,
      status: currentBulkActionFooterBtns.CONSULT.status,
      text: currentBulkActionFooterBtns.CONSULT.text,
      objectsToTotal: currentBulkActionFooterBtns.CONSULT.objectsToTotal,
      commentKey: currentBulkActionFooterBtns.CONSULT.commentKey,
      kind: 'outlined',
      popOverContent: '',
      onClick: handleConsultModalOpen,
    },
    belongsToMe: {
      id: currentBulkActionFooterBtns.BELONGS_TO_ME.id,
      icon: currentBulkActionFooterBtns.BELONGS_TO_ME.icon,
      status: currentBulkActionFooterBtns.BELONGS_TO_ME.status,
      text: currentBulkActionFooterBtns.BELONGS_TO_ME.text,
      objectsToTotal: currentBulkActionFooterBtns.BELONGS_TO_ME.objectsToTotal,
      commentKey: currentBulkActionFooterBtns.BELONGS_TO_ME.commentKey,
      kind: 'outlined',
      popOverContent: '',
      onClick: handleBelongsToMeModalOpen,
    },
    decommission: {
      id: currentBulkActionFooterBtns.DECOMMISSION.id,
      icon: currentBulkActionFooterBtns.DECOMMISSION.icon,
      status: currentBulkActionFooterBtns.DECOMMISSION.status,
      text: currentBulkActionFooterBtns.DECOMMISSION.text,
      objectsToTotal: currentBulkActionFooterBtns.DECOMMISSION.objectsToTotal,
      commentKey: currentBulkActionFooterBtns.DECOMMISSION.commentKey,
      kind: 'outlined',
      popOverContent: '',
      onClick: handleDecommissionModalOpen,
    },
  };

  const renderBulkActionFooterInfoBox = () => {
    const bulkFooterStatsKeys = Object.keys(bulkFooterStats);
    // messages.CERTS_LIST_SELECT_ALL || localize(intl, getMessageKey('CERTS_LIST_SELECT_ALL'))

    const fullSelectedInfo =
      bulkFooterStatsKeys
        ?.filter((key) => bulkFooterStats[key] > 0)
        ?.map((key) => {
          const messageKey =
            BULK_ACTION_FOOTER_OBJECTS_MESSGAE_KEYS[
              `${BULK_ACTION_FOOTER_OBJECTS[key]}${
                bulkFooterStats[key] > 1 ? 's' : ''
              }`
            ];

          return `${
            messages[messageKey] || localize(intl, getMessageKey(messageKey))
          } (${bulkFooterStats[key]})`;
        })
        .join(', ') ||
      messages.CERTS_BULK_NOTHING_SELECTED ||
      localize(intl, getMessageKey('CERTS_BULK_NOTHING_SELECTED'));

    return (
      <Box
        tag='div'
        className='BulkActionFooter-infoBox'
        dataTheme={SYSTEM_DATA_THEMES.DARK}>
        <Box tag='div' className='BulkActionFooter-infoBoxText'>
          <Typography
            kind={isMobileXlargeWidth ? 'body2' : 'body1'}
            className='BulkActionFooter-infoBoxDescription'>
            <span className='BulkActionFooter-infoBoxTitle'>
              {messages.CERTS_BULK_SELECTED ||
                localize(intl, getMessageKey('CERTS_BULK_SELECTED'))}
            </span>
            {`${fullSelectedInfo}`}
          </Typography>
        </Box>
        <ButtonIcon
          size={isMobileXlargeWidth ? 'small' : 'medium'}
          kind='ghost'
          className='BulkActionFooter-closeButton'
          ariaLabel={l('Clear')}
          icon={
            <Icon
              kind='close'
              color='gray-000'
              className='BulkActionFooter-clearSelectionIcon'
            />
          }
          onClick={() => {
            bulkActionClearBtn.onClick();
          }}
          dataTheme='dark'
        />
      </Box>
    );
  };

  const bulkActionButtonCountForMobile = bulkActionBtnsMap?.length || 0;

  const renderButtonMenuForMobile = () => {
    if (bulkActionButtonCountForMobile > BULK_ACTION_BUTTON_COUNT_PER_ROW) {
      if (isMobileMoreChecked) {
        return bulkFooterSortedBtns(bulkActionBtnsMap, isEOCampaign).map(
          (key) => {
            const {
              id,
              status,
              icon,
              text,
              objectsToTotal,
              onClick: onClickFunc,
              commentKey,
            } = bulkActionButtons[key];
            let btnCount =
              objectsToTotal.reduce((total, item) => {
                if (bulkFooterStats[item]) return total + bulkFooterStats[item];

                return total;
              }, 0) || '0';

            if (
              id === currentBulkActionFooterBtns.CONSULT.id &&
              bulkFooterInfo?.showConsultBasedOnConsultCount
            ) {
              btnCount = bulkFooterInfo?.consultCount || '0';
            }

            // Hide menu items with zero count to avoid showing non-actionable options
            if (btnCount === '0' || btnCount === 0) return null;

            return (
              <div
                className='BulkActionFooter-wrapperHolder--isMobileMode'
                key={id}>
                <ButtonCore
                  id={id}
                  dataTheme={SYSTEM_DATA_THEMES.DARK}
                  onClick={() => onClickFunc(id, status, commentKey)}
                  ariaLabel={
                    messages?.[text] || localize(intl, getMessageKey(text))
                  }
                  className='BulkActionFooter-buttonWrapper--isMobileMode'>
                  <Box className='BulkActionFooter-button--isMobileMode'>
                    <Icon kind={icon} color='neutral-000' />
                  </Box>
                  <Typography
                    kind='body4'
                    desktop='body4-d'
                    mobile='body4-d'
                    className='BulkActionFooter-buttonText--isMobileMode'>
                    {`${
                      messages?.[text] || localize(intl, getMessageKey(text))
                    } (${btnCount})`}
                  </Typography>
                </ButtonCore>
              </div>
            );
          }
        );
      }

      return bulkFooterSortedBtns(bulkActionBtnsMap, isEOCampaign)
        .filter((_, index) => index < BULK_ACTION_BUTTON_COUNT_PER_ROW - 1)
        .map((key) => {
          const {
            id,
            status,
            icon,
            text,
            objectsToTotal,
            onClick: onClickFunc,
            commentKey,
          } = bulkActionButtons[key];
          let btnCount =
            objectsToTotal.reduce((total, item) => {
              if (bulkFooterStats[item]) return total + bulkFooterStats[item];

              return total;
            }, 0) || '0';

          if (
            id === currentBulkActionFooterBtns.CONSULT.id &&
            bulkFooterInfo?.showConsultBasedOnConsultCount
          ) {
            btnCount = bulkFooterInfo?.consultCount || '0';
          }

          // Hide menu items with zero count to avoid showing non-actionable options
          if (btnCount === '0' || btnCount === 0) return null;

          return (
            <div
              className='BulkActionFooter-wrapperHolder--isMobileMode'
              key={id}>
              <ButtonCore
                id={id}
                dataTheme={SYSTEM_DATA_THEMES.DARK}
                onClick={() => onClickFunc(id, status, commentKey)}
                ariaLabel={
                  messages?.[text] || localize(intl, getMessageKey(text))
                }
                className='BulkActionFooter-buttonWrapper--isMobileMode'>
                <Box className='BulkActionFooter-button--isMobileMode'>
                  <Icon kind={icon} color='neutral-000' />
                </Box>
                <Typography
                  kind='body4'
                  desktop='body4-d'
                  mobile='body4-d'
                  className='BulkActionFooter-buttonText--isMobileMode'>
                  {`${
                    messages?.[text] || localize(intl, getMessageKey(text))
                  } (${btnCount})`}
                </Typography>
              </ButtonCore>
            </div>
          );
        });
    }

    return bulkFooterSortedBtns(bulkActionBtnsMap, isEOCampaign).map((key) => {
      const {
        id,
        status,
        icon,
        text,
        objectsToTotal,
        onClick: onClickFunc,
        commentKey,
      } = bulkActionButtons[key];
      let btnCount =
        objectsToTotal.reduce((total, item) => {
          if (bulkFooterStats[item]) return total + bulkFooterStats[item];

          return total;
        }, 0) || '0';

      if (
        id === currentBulkActionFooterBtns.CONSULT.id &&
        bulkFooterInfo?.showConsultBasedOnConsultCount
      ) {
        btnCount = bulkFooterInfo?.consultCount || '0';
      }

      // Hide menu items with zero count to avoid showing non-actionable options
      if (btnCount === '0' || btnCount === 0) return null;

      return (
        <div className='BulkActionFooter-wrapperHolder--isMobileMode' key={id}>
          <ButtonCore
            id={id}
            dataTheme={SYSTEM_DATA_THEMES.DARK}
            onClick={() => onClickFunc(id, status, commentKey)}
            ariaLabel={messages?.[text] || localize(intl, getMessageKey(text))}
            className='BulkActionFooter-buttonWrapper--isMobileMode'>
            <Box className='BulkActionFooter-button--isMobileMode'>
              <Icon kind={icon} color='neutral-000' />
            </Box>
            <Typography
              kind='body4'
              desktop='body4-d'
              mobile='body4-d'
              className='BulkActionFooter-buttonText--isMobileMode'>
              {`${
                messages?.[text] || localize(intl, getMessageKey(text))
              } (${btnCount})`}
            </Typography>
          </ButtonCore>
        </div>
      );
    });
  };

  const getMoreLessTextForMobile = (key) =>
    messages.key || localize(intl, getMessageKey(key));

  return (
    <Box tag='div' className={wrapperClass}>
      {isBulkFooterVisible && (
        <>
          <BulkActionCommon
            bulkFooterStats={bulkFooterStats}
            bulkActionCommonAction={bulkActionCommonAction}
            setBulkActionCommonAction={setBulkActionCommonAction}
            handleCommonAction={handleCommonAction}
            bulkPredefinedComments={
              bulkActionCommonAction?.commentKey
                ? predefinedComments[bulkActionCommonAction.commentKey] || []
                : bulkPredefinedComments
            }
            isMobileXlargeWidth={isMobileXlargeWidth}
          />
          <BulkActionEndDate
            bulkFooterStats={bulkFooterStats}
            bulkActionEndDate={bulkActionEndDate}
            setBulkActionEndDate={setBulkActionEndDate}
            handleEndDate={handleEndDate}
            bulkPredefinedComments={
              bulkActionEndDate?.commentKey
                ? predefinedComments[bulkActionEndDate.commentKey] || []
                : bulkPredefinedComments
            }
            isMobileXlargeWidth={isMobileXlargeWidth}
          />
          <BulkActionConsult
            bulkFooterStats={bulkFooterStats}
            bulkActionConsult={bulkActionConsult}
            setBulkActionConsult={setBulkActionConsult}
            handleConsult={handleConsult}
            bulkPredefinedComments={
              bulkActionConsult?.commentKey
                ? predefinedComments[bulkActionConsult.commentKey] || []
                : bulkPredefinedComments
            }
            isMobileXlargeWidth={isMobileXlargeWidth}
          />
          <BulkActionReassign
            bulkFooterStats={bulkFooterStats}
            bulkActionReassign={bulkActionReassign}
            setBulkActionReassign={setBulkActionReassign}
            handleReassign={handleReassign}
            bulkPredefinedComments={
              bulkActionReassign?.commentKey
                ? predefinedComments[bulkActionReassign.commentKey] || []
                : bulkPredefinedComments
            }
            isMobileXlargeWidth={isMobileXlargeWidth}
          />
        </>
      )}
      {renderBulkActionFooterInfoBox()}
      {isMobileXlargeWidth ? (
        <div className='BulkActionFooter-btnBox' ref={bulkActionBtnBoxRef}>
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid -- Link required as per the design */}
          <Link
            key={bulkActionClearBtn.id}
            kind='button'
            size='medium'
            dataTheme={SYSTEM_DATA_THEMES.DARK}
            onClick={bulkActionClearBtn.onClick}
            isDisabled={isDisabled || isSubmittingAction || isLoading}
            text={
              messages?.[bulkActionClearBtn.text] ||
              localize(intl, getMessageKey(bulkActionClearBtn.text))
            }
            className='BulkActionFooter-btnBoxButton BulkActionFooter-btnClearSelection'
          />
          <Box tag='div' className='BulkActionFooter-controlsWrapper'>
            {renderButtonMenuForMobile()}
            {bulkActionButtonCountForMobile >
              BULK_ACTION_BUTTON_COUNT_PER_ROW && (
              <div className='BulkActionFooter-wrapperHolder--isMobileMode'>
                <ButtonCore
                  id='BulkActionMobileMoreButton'
                  dataTheme={SYSTEM_DATA_THEMES.DARK}
                  onClick={() => setIsMobileMoreChecked((prev) => !prev)}
                  ariaLabel={getMoreLessTextForMobile(
                    isMobileMoreChecked ? 'CERTS_BULK_LESS' : 'CERTS_BULK_MORE'
                  )}
                  className='BulkActionFooter-buttonWrapper--isMobileMode'>
                  <Box className='BulkActionFooter-button--isMobileMode BulkActionFooter-button--isSwitched'>
                    <Icon
                      kind={isMobileMoreChecked ? 'ChevronUp' : 'ChevronDown'}
                      color='neutral-000'
                    />
                  </Box>
                  <Typography
                    kind='body4'
                    desktop='body4-d'
                    mobile='body4-d'
                    className='BulkActionFooter-buttonText--isMobileMode'>
                    {getMoreLessTextForMobile(
                      isMobileMoreChecked
                        ? 'CERTS_BULK_LESS'
                        : 'CERTS_BULK_MORE'
                    )}
                  </Typography>
                </ButtonCore>
              </div>
            )}
          </Box>
        </div>
      ) : (
        <div className='BulkActionFooter-btnBox' ref={bulkActionBtnBoxRef}>
          {/* eslint-disable-next-line jsx-a11y/anchor-is-valid -- Link required as per the design */}
          <Link
            key={bulkActionClearBtn.id}
            kind='button'
            size='medium'
            dataTheme={SYSTEM_DATA_THEMES.DARK}
            onClick={bulkActionClearBtn.onClick}
            isDisabled={isDisabled || isSubmittingAction || isLoading}
            text={
              messages?.[bulkActionClearBtn.text] ||
              localize(intl, getMessageKey(bulkActionClearBtn.text))
            }
            className='BulkActionFooter-btnBoxButton BulkActionFooter-btnClearSelection'
          />
          <Box tag='div' className='BulkActionFooter-controlsWrapper'>
            <Box tag='div' className='BulkActionFooter-btnBoxVariableBtns'>
              {bulkFooterSortedBtns(bulkActionBtnsMap, isEOCampaign)
                .filter((_, index) => index < countForActionButtonsAsPerScreen)
                .map((key) => {
                  const {
                    id,
                    status,
                    kind,
                    text,
                    popOverContent,
                    objectsToTotal,
                    onClick: onClickFunc,
                    commentKey,
                  } = bulkActionButtons[key];
                  let btnCount =
                    objectsToTotal.reduce((total, item) => {
                      if (bulkFooterStats[item])
                        return total + bulkFooterStats[item];

                      return total;
                    }, 0) || '0';

                  if (
                    id === currentBulkActionFooterBtns.CONSULT.id &&
                    bulkFooterInfo?.showConsultBasedOnConsultCount
                  ) {
                    btnCount = bulkFooterInfo?.consultCount || '0';
                  }

                  // Hide menu items with zero count to avoid showing non-actionable options
                  if (btnCount === '0' || btnCount === 0) return null;

                  return (
                    <Button
                      key={id}
                      type='button'
                      kind={kind}
                      size='medium'
                      dataTheme={SYSTEM_DATA_THEMES.DARK}
                      onClick={() => onClickFunc(id, status, commentKey)}
                      rightIcon={
                        popOverContent.trim().length > 0 ? (
                          <Icon
                            kind='AlertInfoFilledSeparateColors'
                            size='smallMedium'
                          />
                        ) : null
                      }
                      className='BulkActionFooter-btnBoxButton'>
                      {`${
                        messages?.[text] || localize(intl, getMessageKey(text))
                      } (${btnCount})`}
                      {popOverContent.trim().length > 0 && (
                        <Box
                          tag='span'
                          className='BulkActionFooter-btnBoxButtonPopOver'>
                          {popOverContent}
                        </Box>
                      )}
                    </Button>
                  );
                })}
            </Box>
            {bulkActionBtnsMap.length > countForActionButtonsAsPerScreen && (
              <Box tag='div' className='BulkActionFooter-btnBoxMenu'>
                {showKebabActionBtns && (
                  <div
                    className='BulkActionFooter-btnBoxMenuDropUp'
                    ref={dropUpWrapperForBulkActionKebab}>
                    {bulkFooterSortedBtns(bulkActionBtnsMap, isEOCampaign)
                      .filter(
                        (_, index) => index >= countForActionButtonsAsPerScreen
                      )
                      .map((key) => {
                        const {
                          id,
                          status,
                          icon,
                          text,
                          objectsToTotal,
                          onClick: onClickFunc,
                          commentKey,
                        } = bulkActionButtons[key];
                        let btnCount =
                          objectsToTotal.reduce((total, item) => {
                            if (bulkFooterStats[item])
                              return total + bulkFooterStats[item];

                            return total;
                          }, 0) || '0';

                        if (
                          id === currentBulkActionFooterBtns.CONSULT.id &&
                          bulkFooterInfo?.showConsultBasedOnConsultCount
                        ) {
                          btnCount = bulkFooterInfo?.consultCount || '0';
                        }

                        // Hide menu items with zero count to avoid showing non-actionable options
                        if (btnCount === '0' || btnCount === 0) return null;

                        return (
                          <span
                            key={id}
                            className='BulkActionFooter-btnBoxMenuDropUpListItem'
                            onClick={() => onClickFunc(id, status, commentKey)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                onClickFunc(id, status, commentKey);
                              }
                            }}
                            role='button'
                            tabIndex='0'>
                            <Icon kind={icon} size='smallMedium' />
                            <Typography
                              kind='body2'
                              className='BulkActionFooter-btnBoxMenuDropUpListItemText'>
                              {`${
                                messages?.[text] ||
                                localize(intl, getMessageKey(text))
                              } (${btnCount})`}
                            </Typography>
                          </span>
                        );
                      })}
                  </div>
                )}
                <ButtonIcon
                  size='medium'
                  kind='ghost'
                  dataTheme={SYSTEM_DATA_THEMES.DARK}
                  onClick={() => setShowKebabActionBtns(!showKebabActionBtns)}
                  icon={
                    <Icon
                      kind='MoreVertical'
                      color='gray-000'
                      size='smallMedium'
                    />
                  }
                />
              </Box>
            )}
          </Box>
        </div>
      )}
    </Box>
  );
}

export default BulkActionFooter;
